import { WidgetDescription } from 'controls/designer/features/widgets/types';

type WidgetDescriptionContainer = {
	getWidgetDescription: () => WidgetDescription;
};

export const allWidgets: Record<string, WidgetDescriptionContainer> = {};

const allWidgetsAsync: Record<string, () => Promise<WidgetDescriptionContainer>> = {
	'application-preview': () => import('areas/application/widgets/preview'),
	'application-state': () => import('areas/application/widgets/stateWidget/state'),
	'service-preview': () => import('areas/services/widgets/preview'),
	'service-summary': () => import('areas/services/widgets/summary'),
	'service-summary-grid': () => import('areas/services/widgets/serviceSummaryWidget/service-summary-grid'),
	'service-history': () => import('areas/services/widgets/history'),
	'service-selective': () => import('areas/services/widgets/selectiveServices'),
	'service-grid': () => import('areas/services/widgets/servicesGridWidget'),
	'service-state': () => import('areas/services/widgets/stateWidget/state'),
	'service-events': () => import('areas/services/widgets/serviceEventsWidget'),
	'service-single-graph': () => import('areas/services/widgets/singleGraph'),
	'service-multi-graph': () => import('areas/services/widgets/multiGraph'),
	'service-gauge': () => import('areas/services/widgets/gauge'),
	'service-document': () => import('areas/service-boards/widgets/serviceDocument/serviceDocumentWidget'),
	'metric-compass': () => import('areas/metrics/widgets/Compass/widgetDescription'),
	'metric-dial': () => import('areas/metrics/widgets/Dial/widgetDescription'),
	'metric-common-gauge': () => import('areas/metrics/widgets/CommonGauge/widgetDescription'),
	'metric-single-graph': () => import('areas/service-boards/widgets/metricSingleGraph'),
	'metric-multi-graph-asset': () => import('areas/service-boards/widgets/metricMultigraphAsset'),
	'metric-multi-graph-asset-group': () => import('areas/service-boards/widgets/metricMultigraphAssetGroup'),
	'metric-gauge': () => import('areas/service-boards/widgets/metricGauge'),
	'metric-stacked-chart': () => import('areas/service-boards/widgets/stackChartWidget/stackedChart'),
	'metric-custom': () => import('areas/service-boards/widgets/customMetrics/metricCustom'),
	'metric-barchart': () => import('areas/service-boards/widgets/barchartMetrics/barchartWidget'),
	'metric-table': () => import('./metrics/tableWidget'),
	'metric-sparkline-table': () => import('./metrics/sparklineTable/sparklineTableWidget'),
	'health-single-graph': () => import('areas/assets/widgets/singleGraph'),
	'health-multi-graph': () => import('areas/assets/widgets/multiGraph'),
	'health-gauge': () => import('areas/assets/widgets/gauge'),
	'asset-grid': () => import('areas/assets/widgets/grid'),
	'asset-console': () => import('areas/assets/widgets/console'),
	'asset-state': () => import('areas/assets/widgets/stateWidget/state'),
	'asset-assetgroup': () => import('areas/assetgroups/widgets/assetGroupWidget'),
	'sla': () => import('areas/sla/widgets/sla'),
	'sla-history': () => import('areas/sla/widgets/history'),
	'sla-grid': () => import('areas/sla/widgets/slaGridWidget'),
	'sla-state': () => import('areas/sla/widgets/stateWidget/state'),
	'sla-time': () => import('areas/sla/widgets/time'),
	'kpi-single-graph': () => import('areas/kpi/widgets/singleGraph'),
	'kpi-multi-graph': () => import('areas/kpi/widgets/multiGraph'),
	'kpi-gauge': () => import('areas/kpi/widgets/gauge'),
	'kpi-history': () => import('areas/kpi/widgets/history'),
	'summary-grid': () => import('areas/service-boards/widgets/grids/summary'),
	'data-sources-chart': () => import('controls/designer/features/widgets/datasources/datasourceChartWidget'),
	'event-summary-grid': () => import('areas/service-boards/widgets/grids/eventsSummary'),
	'time': () => import('controls/designer/features/widgets/custom/timeWidget'),
	'ai-query': () => import('controls/designer/features/widgets/ai-query-widget/aiQueryWidget'),
	'gps': () => import('controls/designer/features/widgets/custom/gps/gpsWidget'),
	'web-page': () => import('areas/service-boards/widgets/webPageWidget'),
	'dashboard': () => import('controls/designer/features/widgets/custom/dashboard'),
	'report': () => import('controls/designer/features/widgets/reportWidget'),
	'cloud-services-cost-bar-chart': () => import('controls/designer/features/widgets/cloudServices/costBarChart'),
	'cloud-services-cost-grid': () => import('controls/designer/features/widgets/cloudServices/costGrid'),
	'cloud-services-instances-grid': () => import('controls/designer/features/widgets/cloudServices/instancesGrid'),
	'cloud-services-buggle-chart': () => import('controls/designer/features/widgets/cloudServices/bubbleChart'),
	'cost-grid': () => import('controls/designer/features/widgets/cost/gridWidget'),
	'cost-profile': () => import('controls/designer/features/widgets/cost/profile/costProfileWidget'),
	'budget-cost-bar-chart': () => import('controls/designer/features/widgets/cost/budget/budgetBarChart'),
	'event-state': () => import('areas/summary/events/eventStateDashboardWidget'),
	'cost-pie-chart': () => import('controls/designer/features/widgets/cost/budget/costPieChart'),
	'indicator-dots': () => import('controls/designer/features/widgets/indicatorDots/indicatorDotsWidget'),
	'metric-tile': () => import('controls/designer/features/widgets/metricTile/metricTile'),
	'data-sources-tile': () => import('controls/designer/features/widgets/datasources/tile'),
	'global-time-selector': () => import('controls/designer/features/widgets/globalTimeSelector'),
	'export': () => import('controls/designer/features/widgets/export/widgetDescription'),
};

// 'lazy' mode: each matched file becomes its own async chunk, fetched on first use instead of
// being pulled into app.js eagerly. Without it, every auto-discovered widgetDescription.tsx (e.g.
// the text widget, which drags in the ~3MB TinyMCE editor) was bundled into every page load.
const bundles = require.context('../../../../', true, /widgetDescription$/, 'lazy');

export async function loadWidgetDescriptions() {
	if (Object.keys(allWidgets).length != 0) return;

	for (const fileName of bundles.keys()) {
		const bundle = bundles(fileName) as {
			getWidgetDescription: () => WidgetDescription;
		};

		if (bundle.getWidgetDescription) {
			const description = bundle.getWidgetDescription();
			allWidgets[description.defaultConfig.type] = bundle;
		}
	}

	await Promise.all(
		bundles.keys().map(async (fileName) => {
			const bundle = (await bundles(fileName)) as {
				getWidgetDescription: () => WidgetDescription;
			};

			if (bundle.getWidgetDescription) {
				const description = bundle.getWidgetDescription();
				allWidgets[description.defaultConfig.type] = bundle;
			}
		}),
	);

	await Promise.all(
		Object.keys(allWidgetsAsync).map(async (widgetId) => {
			allWidgets[widgetId] = await allWidgetsAsync[widgetId]();
		}),
	);
}
